const TITLE_MAX = 100;
const BODY_MAX = 500;

const STATUS_KEYS = {
  pending: "notif_status_pending",
  sending: "notif_status_sending",
  done: "notif_status_done",
  failed: "notif_status_failed",
};

const idOf = (row) => row.id || row._id || null;
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Duyuru gönderilebilecek hedefler. Admin ayrıca "herkes"i görür.
 * value biçimi: "all" | "group:<id>" | "hatim:<id>"
 *
 * Grup adı `groupName`, hatim adı `name` alanında gelir — iki uç farklı
 * sözleşme döndürdüğü için ayrı ayrı okunuyor, ortak bir fallback yok:
 * yanlış alandan okunursa etiket boş kalmalı ki sessizce yanlış ad basmasın.
 */
export function buildTargetOptions({ role, groups, templates }) {
  const options = [];

  if (role === "Admin") {
    options.push({
      value: "all",
      kind: "all",
      refId: null,
      labelKey: "notif_target_all",
      label: null,
    });
  }

  (groups || []).forEach((group) => {
    const id = idOf(group);
    if (!id || !group.groupName) return;
    options.push({
      value: `group:${id}`,
      kind: "group",
      refId: String(id),
      labelKey: null,
      label: group.groupName,
    });
  });

  (templates || []).forEach((template) => {
    const id = idOf(template);
    if (!id || !template.name) return;
    options.push({
      value: `hatim:${id}`,
      kind: "hatim",
      refId: String(id),
      labelKey: null,
      label: template.name,
    });
  });

  return options;
}

/**
 * Seçilen her hedef için ayrı bir istek gövdesi.
 * Sunucu tek seferde tek kitle alıyor; çoklu seçim istemcide N isteğe bölünüyor.
 * Böylece her duyuru kendi kaydını oluşturur ve geçmişte hangi gruba gittiği görünür.
 */
export function buildSendPayloads(selected, options) {
  const byValue = new Map((options || []).map((o) => [o.value, o]));
  return (selected || [])
    .map((v) => byValue.get(v))
    .filter(Boolean)
    .map((o) =>
      o.kind === "all"
        ? { audienceType: "all" }
        : { audienceType: o.kind, audienceRefId: o.refId }
    );
}

/**
 * Geçmiş satırında gösterilecek hedef adı.
 * Otomatik olay kayıtları (audience.type === "users") belirli bir gruba
 * gönderilmediği için hedef göstermez.
 */
export function resolveTargetLabel({ audience, groups, templates }) {
  const type = audience && audience.type;
  if (!type || type === "users") return null;

  if (type === "all") return { labelKey: "notif_target_all", label: null };

  // Sunucu artık hedefin gönderim anındaki adını kayda yazıyor. Buna
  // güveniyoruz: Admin yönetmediği grupların duyurularını da görüyor ve o
  // grupları kendi listesinde bulamaz; ayrıca grup sonradan yeniden
  // adlandırılsa bile geçmişte o günkü ad görünmeli.
  if (audience.name) return { labelKey: null, label: audience.name };

  // Ad kaydedilmeden önce oluşmuş eski kayıtlar için listeden aramayı sürdür.
  const list = type === "group" ? groups || [] : templates || [];
  const match = list.find((row) => sameId(idOf(row), audience.refId));
  if (!match) return { labelKey: "notif_target_unknown", label: null };

  return { labelKey: null, label: type === "group" ? match.groupName : match.name };
}

/** Gönder butonu etkin mi? */
export function canSubmit({ selected, title, body, isLoading }) {
  if (isLoading) return false;
  if (!Array.isArray(selected) || selected.length === 0) return false;
  const t = (title || "").trim();
  const b = (body || "").trim();
  if (!t.length || t.length > TITLE_MAX) return false;
  if (!b.length || b.length > BODY_MAX) return false;
  return true;
}

/** Durum rozetinin çeviri anahtarı. */
export function statusLabelKey(status) {
  return STATUS_KEYS[status] || STATUS_KEYS.pending;
}

export const TITLE_MAX_LENGTH = TITLE_MAX;
export const BODY_MAX_LENGTH = BODY_MAX;
